import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  Drawer,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SystemUpdateAltRoundedIcon from '@mui/icons-material/SystemUpdateAltRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../api/axiosInstance';
import PageHeader from '../../components/common/PageHeader';

const AGENT_RELEASE_NOTES = [
  {
    version: '0.1.17',
    title: 'Inventaire périphériques',
    items: [
      'Remontée des périphériques USB et Plug & Play avec nom, fabricant, classe et identifiant PNP.',
      'Remontée des écrans via EDID quand disponible, avec modèle et numéro de série exposé par le constructeur.',
      'Remontée des imprimantes installées avec driver, port et IP quand le port TCP/IP est disponible.',
      'Nouvelle section Périphériques dans l accordéon AgentBOX.',
    ],
  },
  {
    version: '0.1.16',
    title: 'Inventaire et pilotage enrichis',
    items: [
      'Rattachement du site dès le déploiement de l agent, avec support site Hudu ou site personnalisé.',
      'Inventaire réseau détaillé : toutes les IP locales, IP publique, interfaces et adresses MAC.',
      'Inventaire stockage : disques multiples avec espace utilisé et capacité totale.',
      'Informations matériel complètes : vendor, modèle et numéro de série.',
      'Statut Windows activé / non activé remonté dans le tableau.',
    ],
  },
  {
    version: '0.1.15',
    title: 'Actions distantes',
    items: [
      'Lancement des mises à jour OS depuis la liste des mises à jour détectées.',
      'Suivi des étapes de mise à jour agent avec statut success, failed ou in progress.',
      'Commande de redémarrage à distance avec confirmation de sécurité.',
      'Suppression manuelle des appareils obsolètes ou offline depuis le tableau.',
    ],
  },
  {
    version: '0.1.14',
    title: 'Utilisateurs et état système',
    items: [
      'Remontée du uptime et de la date d installation OS pour estimer l âge de la machine.',
      'Liste des utilisateurs et groupes locaux ou Active Directory quand disponible.',
      'Dernière connexion utilisateur et indicateurs administrateur.',
      'Accordéon de détail par appareil pour consulter inventaire, mises à jour, utilisateurs et groupes.',
    ],
  },
];

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatBytes(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = n;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatUptime(value) {
  const totalSeconds = Number(value || 0);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '-';
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) return `${days} j ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}

function formatOsAge(value) {
  const days = Number(value || 0);
  if (!Number.isFinite(days) || days <= 0) return '-';
  const years = Math.floor(days / 365);
  const remainingDays = days % 365;
  if (years > 0) return `${years} an${years > 1 ? 's' : ''} ${remainingDays} j`;
  return `${days} j`;
}

function formatPercent(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return '-';
  return `${n.toFixed(1)} %`;
}

function getLoggedUsers(agent = {}) {
  const users = agent.lastInventory?.loggedUsers || agent.lastInventory?.raw?.loggedUsers || [];
  return Array.isArray(users) ? users.filter(Boolean) : [];
}

function formatLoggedUser(user = {}) {
  return cleanText(user.fullName || [user.domain, user.username].filter(Boolean).join('\\') || user.username || '-');
}

function cleanText(value) {
  let text = String(value || '');
  if (!text) return text;
  const replacements = [
    [/Mise � jour/g, 'Mise à jour'],
    [/mises � jour/g, 'mises à jour'],
    [/mise � jour/g, 'mise à jour'],
    [/s�lection/g, 'sélection'],
    [/d�sactiv/g, 'désactiv'],
    [/D�sactiv/g, 'Désactiv'],
    [/d�marr/g, 'démarr'],
    [/D�marr/g, 'Démarr'],
    [/termin�/g, 'terminé'],
    [/Termin�/g, 'Terminé'],
    [/demand�/g, 'demandé'],
    [/Demand�/g, 'Demandé'],
    [/Red�marrage/g, 'Redémarrage'],
    [/red�marrage/g, 'redémarrage'],
    [/V�rification/g, 'Vérification'],
    [/v�rification/g, 'vérification'],
    [/r�ussi/g, 'réussi'],
    [/R�ussi/g, 'Réussi'],
    [/�chec/g, 'échec'],
    [/Invit�s/g, 'Invités'],
    [/Invit�/g, 'Invité'],
    [/invit�s/g, 'invités'],
    [/invit�/g, 'invité'],
    [/Administrateurs du domaine/g, 'Administrateurs du domaine'],
  ];
  replacements.forEach(([pattern, replacement]) => {
    text = text.replace(pattern, replacement);
  });
  return text;
}

function getDirectoryUsers(agent = {}) {
  const users = agent.lastInventory?.directoryUsers
    || agent.lastInventory?.raw?.directoryUsers
    || agent.lastInventory?.localUsers
    || agent.lastInventory?.raw?.localUsers
    || [];
  return Array.isArray(users) ? users.filter(Boolean) : [];
}

function getDirectoryGroups(agent = {}) {
  const groups = agent.lastInventory?.directoryGroups
    || agent.lastInventory?.raw?.directoryGroups
    || agent.lastInventory?.localGroups
    || agent.lastInventory?.raw?.localGroups
    || [];
  return Array.isArray(groups) ? groups.filter(Boolean) : [];
}

function formatShortDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function getAgentOrgId(agent = {}) {
  return agent.Organization?._id || agent.Organization || '';
}

function getAgentOrgName(agent = {}) {
  return agent.Organization?.name || 'Sans client';
}

function getAgentOsLabel(agent = {}) {
  const raw = [agent.os?.name, agent.os?.version, agent.os?.arch].filter(Boolean).join(' ')
    || [agent.lastInventory?.os?.name, agent.lastInventory?.os?.version, agent.lastInventory?.os?.arch].filter(Boolean).join(' ')
    || '';
  const value = raw.toLowerCase();
  if (value.includes('windows')) {
    if (value.includes('2025')) return 'Windows Server 2025';
    if (value.includes('2022')) return 'Windows Server 2022';
    if (value.includes('2019')) return 'Windows Server 2019';
    if (value.includes('2016')) return 'Windows Server 2016';
    if (value.includes('2012')) return 'Windows Server 2012';
    if (value.includes('windows 11')) return 'Windows 11';
    if (value.includes('windows 10')) return 'Windows 10';
    return 'Windows';
  }
  if (value.includes('debian')) return 'Linux Debian';
  if (value.includes('ubuntu')) return 'Linux Ubuntu';
  if (value.includes('red hat') || value.includes('rhel')) return 'Linux Red Hat';
  if (value.includes('centos')) return 'Linux CentOS';
  if (value.includes('rocky')) return 'Linux Rocky';
  if (value.includes('almalinux')) return 'Linux AlmaLinux';
  if (value.includes('linux')) return 'Linux';
  return raw || 'Non renseigné';
}

function getAgentSiteLabel(agent = {}) {
  return agent.site || agent.siteRef?.name || 'Sans site';
}

function normalizeUpdateIdentity(value = '') {
  return String(value || '').trim().toLowerCase();
}

function findOSUpdateJob(agent = {}, item = {}) {
  const jobs = Array.isArray(agent.osUpdateJobs) ? agent.osUpdateJobs : [];
  const itemName = normalizeUpdateIdentity(item.name);
  const itemKb = normalizeUpdateIdentity(item.kb);
  return [...jobs].reverse().find((job) => (
    (itemKb && normalizeUpdateIdentity(job.kb) === itemKb)
    || (itemName && normalizeUpdateIdentity(job.name) === itemName)
  )) || null;
}

function getOSUpdateJobMeta(status) {
  const normalized = status || '';
  if (normalized === 'success') return { label: 'Installée', color: 'success' };
  if (normalized === 'failed') return { label: 'Erreur', color: 'error' };
  if (normalized === 'rebooting') return { label: 'Reboot', color: 'warning' };
  if (['queued', 'sent', 'running'].includes(normalized)) return { label: 'En cours', color: 'warning' };
  return { label: '', color: 'default' };
}

function getWindowsActivation(agent = {}) {
  return agent.lastInventory?.activation?.windows
    || agent.lastInventory?.raw?.activation?.windows
    || null;
}

function getActivationMeta(agent = {}) {
  const activation = getWindowsActivation(agent);
  const osFamily = String(agent.os?.family || agent.lastInventory?.os?.family || '').toLowerCase();
  const osName = String(agent.os?.name || agent.lastInventory?.os?.name || '').toLowerCase();
  const isWindows = osFamily === 'windows' || osName.includes('windows');

  if (!isWindows) return { label: '-', color: 'default', variant: 'outlined', title: 'Non applicable' };
  if (!activation) return { label: 'Inconnu', color: 'default', variant: 'outlined', title: 'Statut activation non remonté' };
  if (activation.activated) return { label: 'Activé', color: 'success', variant: 'filled', title: activation.status || 'Licensed' };
  return { label: 'Non activé', color: 'error', variant: 'filled', title: activation.status || `LicenseStatus ${activation.licenseStatus ?? '-'}` };
}

function getPendingUpdateCount(agent = {}) {
  return Number(agent.lastInventory?.updates?.pendingCount ?? 0);
}

function getActivationFilterValue(agent = {}) {
  const meta = getActivationMeta(agent);
  if (meta.label === 'Activé') return 'active';
  if (meta.label === 'Non activé') return 'inactive';
  if (meta.label === '-') return 'not_applicable';
  return 'unknown';
}

function formatIps(ips) {
  if (!Array.isArray(ips) || ips.length === 0) return [];
  return ips.map((ip) => String(ip || '').trim()).filter(Boolean);
}

function normalizeIpValue(value) {
  return String(value || '').trim().replace(/^::ffff:/, '').split('/')[0];
}

function isUsefulLocalIp(ip) {
  const value = normalizeIpValue(ip);
  return value && !value.includes(':') && !value.startsWith('127.') && value !== '0.0.0.0';
}

function getAgentLocalIps(agent) {
  const networks = Array.isArray(agent.lastInventory?.network) ? agent.lastInventory.network : [];
  const values = [];
  networks.forEach((network) => {
    (Array.isArray(network.ips) ? network.ips : []).forEach((ip) => {
      const value = normalizeIpValue(ip);
      if (isUsefulLocalIp(value) && !values.includes(value)) values.push(value);
    });
  });
  const withoutApipa = values.filter((ip) => !ip.startsWith('169.254.'));
  if (withoutApipa.length > 0) return withoutApipa;
  if (values.length > 0) return values;
  return agent.ipAddress ? [normalizeIpValue(agent.ipAddress)] : [];
}

function DetailRow({ label, value, mono = false }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component={mono ? 'code' : 'span'} sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{value || '-'}</Typography>
    </Stack>
  );
}

function AgentEsetPanel({ agent }) {
  const esetDevices = Array.isArray(agent.esetDevices) ? agent.esetDevices : (agent.esetDevice ? [agent.esetDevice] : []);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>ESET</Typography>
          <Chip
            size="small"
            color={esetDevices.length ? 'success' : 'default'}
            variant={esetDevices.length ? 'filled' : 'outlined'}
            label={esetDevices.length ? `${esetDevices.length} match` : 'Aucun match'}
          />
        </Stack>
        {esetDevices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun device ESET trouvé avec le numéro de série de cette machine.</Typography>
        ) : esetDevices.map((device, index) => {
          const components = Array.isArray(device.components) ? device.components : [];
          return (
            <Paper key={device._id || `${device.name}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper' }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 900, wordBreak: 'break-word' }}>{device.name || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {device.Organization?.name || device.tenantName || '-'}
                    </Typography>
                  </Box>
                  {device.matchedSerial ? <Chip size="small" variant="outlined" label={`SN ${device.matchedSerial}`} /> : null}
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                  <DetailRow label="Tenant ESET" value={device.tenantName || '-'} />
                  <DetailRow label="Dernière synchro ESET" value={formatDateTime(device.lastSync)} />
                  <DetailRow label="IP LAN" value={device.ipLAN || '-'} mono />
                  <DetailRow label="IP WAN" value={device.ipWAN || '-'} mono />
                  <DetailRow label="Fabricant ESET" value={device.hardware?.manufacturer || '-'} />
                  <DetailRow label="Serial ESET" value={device.hardware?.serialNumber || '-'} mono />
                </Stack>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {components.length ? components.map((component, componentIndex) => (
                    <Chip
                      key={`${component.name || 'component'}-${componentIndex}`}
                      size="small"
                      variant="outlined"
                      label={[component.name, component.version].filter(Boolean).join(' · ')}
                    />
                  )) : <Chip size="small" variant="outlined" label="Aucun composant remonté" />}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
}

function AgentPeripheralsPanel({ agent }) {
  const peripherals = Array.isArray(agent.lastInventory?.peripherals)
    ? agent.lastInventory.peripherals
    : (Array.isArray(agent.lastInventory?.raw?.peripherals) ? agent.lastInventory.raw.peripherals : []);
  const byType = peripherals.reduce((acc, item) => {
    const type = item.type || 'Autre';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Périphériques</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`${peripherals.length} périphérique(s)`} />
            {Object.entries(byType).slice(0, 6).map(([type, count]) => (
              <Chip key={type} size="small" variant="outlined" label={`${type} ${count}`} />
            ))}
          </Stack>
        </Stack>
        {peripherals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun périphérique remonté.</Typography>
        ) : (
          <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th': { textAlign: 'left', fontSize: 12, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }, '& td': { fontSize: 12, borderBottom: '1px solid', borderColor: 'divider', py: 0.75, pr: 1, verticalAlign: 'top' } }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th">Type</Box>
                  <Box component="th">Nom</Box>
                  <Box component="th">Fabricant / modèle</Box>
                  <Box component="th">Serial</Box>
                  <Box component="th">Interface</Box>
                  <Box component="th">IP / port</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {peripherals.map((item, index) => (
                  <Box component="tr" key={`${item.type || 'peripheral'}-${item.name || item.serial || index}`}>
                    <Box component="td">
                      <Chip size="small" variant="outlined" label={item.type || '-'} sx={{ height: 20, fontSize: 11 }} />
                    </Box>
                    <Box component="td">
                      <Stack spacing={0.15}>
                        <Typography variant="caption" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{cleanText(item.name) || '-'}</Typography>
                        {item.class || item.source ? (
                          <Typography variant="caption" color="text.secondary">{cleanText([item.class, item.source].filter(Boolean).join(' · '))}</Typography>
                        ) : null}
                      </Stack>
                    </Box>
                    <Box component="td">{cleanText([item.manufacturer, item.model].filter(Boolean).join(' · ')) || '-'}</Box>
                    <Box component="td">
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" component="code">{item.serial || '-'}</Typography>
                        {item.serial && !item.serialReliable ? <Chip size="small" variant="outlined" label="estimé" sx={{ height: 18, fontSize: 10 }} /> : null}
                      </Stack>
                    </Box>
                    <Box component="td">{item.interface || '-'}</Box>
                    <Box component="td">{[item.ipAddress, item.portName].filter(Boolean).join(' · ') || '-'}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}


function normalizeAgentService(item) {
  if (!item || typeof item !== 'object') return null;
  const name = item.name || item.n || item.serviceName || item.unit || '';
  const displayName = item.displayName || item.d || item.label || name;
  const status = item.status || item.state || item.s || item.active || '';
  const startType = item.startType || item.startMode || item.t || item.startupType || '';
  const account = item.account || item.startName || item.a || '';
  const processId = item.processId ?? item.pid ?? item.p ?? '';
  const loadState = item.loadState || item.l || '';
  const subState = item.subState || item.u || '';
  return { name, displayName, status, startType, account, processId, loadState, subState };
}

function getAgentServices(agent) {
  const inventory = agent.lastInventory || {};
  const rawServices = Array.isArray(inventory.services)
    ? inventory.services
    : (Array.isArray(inventory.raw?.services) ? inventory.raw.services : []);
  return rawServices.map(normalizeAgentService).filter(Boolean).sort((a, b) => {
    const sa = String(a.status || '').toLowerCase();
    const sb = String(b.status || '').toLowerCase();
    const ar = ['running', 'active'].includes(sa) ? 0 : 1;
    const br = ['running', 'active'].includes(sb) ? 0 : 1;
    return ar - br || String(a.displayName || a.name || '').localeCompare(String(b.displayName || b.name || ''), 'fr');
  });
}

function getServiceStatusMeta(status) {
  const normalized = String(status || '').toLowerCase();
  if (['running', 'active'].includes(normalized)) return { label: status || 'Actif', color: 'success', variant: 'filled' };
  if (['stopped', 'inactive', 'failed'].includes(normalized)) return { label: status || 'Arrêté', color: 'default', variant: 'outlined' };
  if (['paused', 'degraded', 'activating', 'deactivating'].includes(normalized)) return { label: status || 'Attention', color: 'warning', variant: 'outlined' };
  return { label: status || '-', color: 'default', variant: 'outlined' };
}

function AgentServicesPanel({ agent, onRequestServiceAction, serviceActionPending }) {
  const services = getAgentServices(agent);
  const runningCount = services.filter((service) => ['running', 'active'].includes(String(service.status || '').toLowerCase())).length;
  const stoppedCount = services.filter((service) => ['stopped', 'inactive', 'failed'].includes(String(service.status || '').toLowerCase())).length;
  const otherCount = Math.max(services.length - runningCount - stoppedCount, 0);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Services</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`${services.length} service(s)`} />
            <Chip size="small" color="success" variant="outlined" label={`${runningCount} actifs`} />
            {stoppedCount > 0 ? <Chip size="small" variant="outlined" label={`${stoppedCount} arrêtés`} /> : null}
            {otherCount > 0 ? <Chip size="small" color="warning" variant="outlined" label={`${otherCount} autre${otherCount > 1 ? 's' : ''}`} /> : null}
          </Stack>
        </Stack>
        {services.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun service remonté.</Typography>
        ) : (
          <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th': { textAlign: 'left', fontSize: 12, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', py: 0.75, pr: 1 }, '& td': { fontSize: 12, borderBottom: '1px solid', borderColor: 'divider', py: 0.75, pr: 1, verticalAlign: 'top' } }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ width: '32%' }}>Service</Box>
                  <Box component="th" sx={{ width: 110 }}>Statut</Box>
                  <Box component="th" sx={{ width: 130 }}>Démarrage</Box>
                  <Box component="th">Compte / unité</Box>
                  <Box component="th" sx={{ width: 90 }}>PID</Box>
                  <Box component="th" sx={{ width: 92, textAlign: 'right' }}>Action</Box>
                </Box>
              </Box>
              <Box component="tbody">
                {services.map((service, index) => {
                  const meta = getServiceStatusMeta(service.status);
                  const normalizedStatus = String(service.status || '').toLowerCase();
                  const isRunning = ['running', 'active'].includes(normalizedStatus);
                  const isStopped = ['stopped', 'inactive', 'failed'].includes(normalizedStatus);
                  const pendingKey = `${agent.agentId}:${service.name || service.displayName}`;
                  const pending = serviceActionPending === pendingKey;
                  return (
                    <Box component="tr" key={`${service.name || service.displayName || 'service'}-${index}`}>
                      <Box component="td">
                        <Stack spacing={0.15}>
                          <Typography variant="caption" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{cleanText(service.displayName || service.name) || '-'}</Typography>
                          {service.name && service.name !== service.displayName ? (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{cleanText(service.name)}</Typography>
                          ) : null}
                        </Stack>
                      </Box>
                      <Box component="td"><Chip size="small" color={meta.color} variant={meta.variant} label={meta.label} sx={{ height: 20, fontSize: 11 }} /></Box>
                      <Box component="td">{cleanText(service.startType) || '-'}</Box>
                      <Box component="td">{cleanText([service.account, service.loadState, service.subState].filter(Boolean).join(' · ')) || '-'}</Box>
                      <Box component="td"><Typography variant="caption" component="code">{service.processId || '-'}</Typography></Box>
                      <Box component="td" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Tooltip title="Démarrer le service">
                          <span>
                            <IconButton size="small" color="success" disabled={pending || !service.name || isRunning} onClick={() => onRequestServiceAction?.(agent, service, 'start')}>
                              <PlayArrowRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Arrêter le service">
                          <span>
                            <IconButton size="small" color="error" disabled={pending || !service.name || isStopped} onClick={() => onRequestServiceAction?.(agent, service, 'stop')}>
                              <StopRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function AgentInventoryPanel({ agent, onRequestOSUpdate, onRequestServiceAction, serviceActionPending }) {
  const inventory = agent.lastInventory || {};
  const hardware = inventory.hardware || {};
  const activation = getWindowsActivation(agent);
  const activationMeta = getActivationMeta(agent);
  const updates = inventory.updates || {};
  const updateItems = Array.isArray(updates.items) ? updates.items : [];
  const disks = Array.isArray(inventory.disks) ? inventory.disks : [];
  const networks = Array.isArray(inventory.network) ? inventory.network : [];
  const loggedUsers = getLoggedUsers(agent);
  const osInfo = inventory.os || agent.os || {};
  const directoryUsers = getDirectoryUsers(agent);
  const directoryGroups = getDirectoryGroups(agent);
  const sortedDirectoryUsers = [...directoryUsers].sort((a, b) => {
    if (Boolean(a.admin) !== Boolean(b.admin)) return a.admin ? -1 : 1;
    const ad = a.lastLogonAt ? new Date(a.lastLogonAt).getTime() : 0;
    const bd = b.lastLogonAt ? new Date(b.lastLogonAt).getTime() : 0;
    return bd - ad || String(a.username || '').localeCompare(String(b.username || ''), 'fr');
  });

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={1.25}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Matériel</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <DetailRow label="Vendor" value={hardware.vendor || hardware.manufacturer || '-'} />
              <DetailRow label="Serial number" value={hardware.serialNumber || '-'} mono />
              <DetailRow label="Modèle" value={hardware.model || hardware.productName || '-'} />
              <DetailRow label="Produit" value={hardware.productName || '-'} />
              <DetailRow label="Installation OS" value={osInfo.installDate ? `${formatDateTime(osInfo.installDate)} (${formatOsAge(osInfo.installAgeDays)})` : '-'} />
              <Stack spacing={0.25} sx={{ minWidth: 180 }}>
                <Typography variant="caption" color="text.secondary">Utilisateur connecté</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {loggedUsers.length > 0 ? loggedUsers.map((user, index) => (
                    <Chip key={`${formatLoggedUser(user)}-${index}`} size="small" variant="outlined" label={formatLoggedUser(user)} />
                  )) : <Typography variant="body2" sx={{ fontWeight: 700 }}>-</Typography>}
                </Stack>
              </Stack>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">Activation Windows</Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" color={activationMeta.color} variant={activationMeta.variant} label={activationMeta.label} />
                  {activation?.productName ? <Typography variant="caption" color="text.secondary">{activation.productName}</Typography> : null}
                  {activation?.partialProductKey ? <Chip size="small" variant="outlined" label={`Key ${activation.partialProductKey}`} /> : null}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <AgentEsetPanel agent={agent} />
        <AgentPeripheralsPanel agent={agent} />
        <AgentServicesPanel agent={agent} onRequestServiceAction={onRequestServiceAction} serviceActionPending={serviceActionPending} />
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Mises à jour</Typography>
              <Stack direction="row" spacing={0.75}>
                <Chip size="small" color={Number(updates.pendingCount || 0) > 0 ? 'warning' : 'success'} variant={Number(updates.pendingCount || 0) > 0 ? 'filled' : 'outlined'} label={`${Number(updates.pendingCount || 0)} à faire`} />
                {updates.source ? <Chip size="small" variant="outlined" label={updates.source} /> : null}
              </Stack>
            </Stack>
            {Array.isArray(updates.warnings) && updates.warnings.length > 0 ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>{updates.warnings.join(' | ')}</Alert>
            ) : null}
            {updateItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Aucune mise à jour détaillée remontée.</Typography>
            ) : (
              <Stack spacing={0.75} sx={{ maxHeight: 260, overflow: 'auto', pr: 0.5 }}>
                {updateItems.map((item, index) => {
                  const job = findOSUpdateJob(agent, item);
                  const meta = getOSUpdateJobMeta(job?.status);
                  const busy = ['queued', 'sent', 'running'].includes(job?.status);
                  return (
                    <Paper key={`${item.name || 'update'}-${index}`} variant="outlined" sx={{ p: 1, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: 'break-word' }}>{cleanText(item.name) || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            {cleanText([item.currentVersion ? `Actuelle: ${item.currentVersion}` : '', item.version ? `Cible: ${item.version}` : '', item.repository || '', item.kb || '', item.severity || ''].filter(Boolean).join(' · '))}
                          </Typography>
                          {job?.message || job?.error ? (
                            <Typography variant="caption" color={job.error ? 'error' : 'text.secondary'} sx={{ display: 'block', wordBreak: 'break-word' }}>
                              {cleanText(job.error || job.message)}
                            </Typography>
                          ) : null}
                        </Box>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {item.kb ? <Chip size="small" variant="outlined" label={item.kb} /> : null}
                          {job?.status ? <Chip size="small" color={meta.color} label={meta.label} /> : null}
                          <Tooltip title={busy ? 'Installation déjà demandée' : 'Installer uniquement cette mise à jour'}>
                            <span>
                              <IconButton size="small" color="secondary" disabled={busy} onClick={() => onRequestOSUpdate?.(agent, item)}>
                                <UpdateRoundedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Utilisateurs et connexions</Typography>
                <Chip size="small" variant="outlined" label={`${directoryUsers.length} user(s)`} />
              </Stack>
              {directoryUsers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucun utilisateur remonté.</Typography>
              ) : (
                <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th': { textAlign: 'left', fontSize: 12, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', py: 0.75 }, '& td': { fontSize: 12, borderBottom: '1px solid', borderColor: 'divider', py: 0.75, pr: 1 } }}>
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th">Utilisateur</Box>
                        <Box component="th">Admin</Box>
                        <Box component="th">Dernière connexion</Box>
                        <Box component="th">Statut</Box>
                        <Box component="th">Source</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {sortedDirectoryUsers.map((user, index) => (
                        <Box component="tr" key={`${user.username || 'user'}-${index}`}>
                          <Box component="td">
                            <Stack spacing={0.1}>
                              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                {cleanText([user.domain, user.username].filter(Boolean).join('\\') || user.userPrincipalName || user.username || '-')}
                              </Typography>
                              {user.fullName || user.userPrincipalName ? (
                                <Typography variant="caption" color="text.secondary">{cleanText(user.fullName || user.userPrincipalName)}</Typography>
                              ) : null}
                            </Stack>
                          </Box>
                          <Box component="td">
                            {user.admin ? (
                              <Tooltip title={Array.isArray(user.adminGroups) ? cleanText(user.adminGroups.join(', ')) : ''}>
                                <Chip size="small" color="error" label="Admin" sx={{ height: 20, fontSize: 11 }} />
                              </Tooltip>
                            ) : <Chip size="small" variant="outlined" label="-" sx={{ height: 20, fontSize: 11 }} />}
                          </Box>
                          <Box component="td">{formatShortDateTime(user.lastLogonAt)}</Box>
                          <Box component="td">
                            <Chip size="small" color={user.disabled ? 'default' : 'success'} variant={user.disabled ? 'outlined' : 'filled'} label={user.disabled ? 'Désactivé' : 'Actif'} sx={{ height: 20, fontSize: 11 }} />
                          </Box>
                          <Box component="td">{user.source || '-'}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Groupes</Typography>
                <Chip size="small" variant="outlined" label={`${directoryGroups.length} groupe(s)`} />
              </Stack>
              {directoryGroups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucun groupe remonté.</Typography>
              ) : (
                <Stack spacing={0.75} sx={{ maxHeight: 240, overflow: 'auto', pr: 0.5 }}>
                  {directoryGroups.map((group, index) => (
                    <Paper key={`${group.name || 'group'}-${index}`} variant="outlined" sx={{ p: 1, borderRadius: 1.5 }}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{cleanText(group.name) || '-'}</Typography>
                          {group.admin ? <Chip size="small" color="error" label="Admin" sx={{ height: 20, fontSize: 11 }} /> : null}
                          {group.source ? <Chip size="small" variant="outlined" label={group.source} sx={{ height: 20, fontSize: 11 }} /> : null}
                        </Stack>
                        {Array.isArray(group.members) && group.members.length > 0 ? (
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {cleanText(group.members.slice(0, 12).join(', '))}{group.members.length > 12 ? ` +${group.members.length - 12}` : ''}
                          </Typography>
                        ) : <Typography variant="caption" color="text.secondary">Aucun membre remonté</Typography>}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Disques</Typography>
                <Chip size="small" variant="outlined" label={`${disks.length} disque(s)`} />
              </Stack>
              {disks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée disque remontée.</Typography>
              ) : disks.map((disk, index) => {
                const total = Number(disk.totalBytes || 0);
                const free = Number(disk.freeBytes || 0);
                const used = Math.max(total - free, 0);
                const percent = Number(disk.usedPercent || (total > 0 ? (used / total) * 100 : 0));
                return (
                  <Paper key={`${disk.mount || disk.name || 'disk'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{disk.mount || disk.name || `Disque ${index + 1}`}</Typography>
                        <Chip size="small" label={formatPercent(percent)} color={percent >= 90 ? 'error' : percent >= 75 ? 'warning' : 'success'} variant="outlined" />
                      </Stack>
                      <Box sx={{ height: 7, borderRadius: 999, bgcolor: 'grey.200', overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(Math.max(percent, 0), 100)}%`, height: '100%', bgcolor: percent >= 90 ? 'error.main' : percent >= 75 ? 'warning.main' : 'success.main' }} />
                      </Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                        <DetailRow label="Utilisé / total" value={`${formatBytes(used)} / ${formatBytes(total)}`} />
                        <DetailRow label="Libre" value={formatBytes(free)} />
                        <DetailRow label="FS" value={disk.filesystem || '-'} />
                        <DetailRow label="Nom" value={disk.name || '-'} mono />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Réseau</Typography>
                <Chip size="small" variant="outlined" label={`${networks.length} carte(s)`} />
              </Stack>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <DetailRow label="IP publique/API" value={agent.lastRemoteAddress || '-'} mono />
                <DetailRow label="IP principale" value={agent.ipAddress || '-'} mono />
              </Stack>
              {networks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Aucune donnée réseau remontée.</Typography>
              ) : networks.map((network, index) => {
                const ips = formatIps(network.ips);
                return (
                  <Paper key={`${network.name || 'net'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'background.paper' }}>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{network.name || `Carte ${index + 1}`}</Typography>
                        <Typography variant="caption" component="code" sx={{ color: 'text.secondary' }}>{network.mac || 'MAC -'}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {ips.length ? ips.map((ip) => <Chip key={ip} size="small" variant="outlined" label={ip} />) : <Chip size="small" variant="outlined" label="Aucune IP" />}
                      </Stack>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        <DetailRow label="Reçu" value={formatBytes(network.rxBytes)} />
                        <DetailRow label="Envoyé" value={formatBytes(network.txBytes)} />
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Stack>

      </Stack>
    </Box>
  );
}

function getStatusColor(status) {
  if (status === 'online') return 'success';
  if (status === 'offline') return 'error';
  return 'default';
}

function getUpdateStatusMeta(status) {
  const normalized = status || 'idle';
  if (normalized === 'success' || normalized === 'up_to_date') return { label: 'Success', color: 'success' };
  if (normalized === 'failed') return { label: 'Failed', color: 'error' };
  if (['pending', 'acknowledged', 'checking', 'restarting'].includes(normalized)) return { label: 'In progress', color: 'warning' };
  return { label: '-', color: 'default' };
}

function getWindowsUpdateStatusMeta(status) {
  const normalized = status || 'idle';
  if (normalized === 'success') return { label: 'WU OK', color: 'success' };
  if (normalized === 'failed') return { label: 'WU erreur', color: 'error' };
  if (normalized === 'not_applicable') return { label: 'Non applicable', color: 'default' };
  if (['pending', 'acknowledged', 'running', 'rebooting'].includes(normalized)) return { label: 'WU en cours', color: 'warning' };
  return { label: 'Windows Update', color: 'primary' };
}

function getStepStatusLabel(status) {
  const normalized = status || 'idle';
  if (normalized === 'pending') return 'En attente';
  if (normalized === 'acknowledged') return 'Recu';
  if (normalized === 'checking') return 'Verification';
  if (normalized === 'restarting') return 'Redemarrage';
  if (normalized === 'success') return 'Succes';
  if (normalized === 'failed') return 'Erreur';
  if (normalized === 'up_to_date') return 'A jour';
  return normalized || '-';
}

function getRebootStatusMeta(status) {
  const normalized = status || 'idle';
  if (normalized === 'success') return { label: 'Reboot OK', color: 'success' };
  if (normalized === 'failed') return { label: 'Reboot failed', color: 'error' };
  if (['pending', 'acknowledged', 'rebooting'].includes(normalized)) return { label: 'Rebooting', color: 'warning' };
  return { label: '-', color: 'default' };
}

function getUpdateTooltip(agent) {
  const parts = [];
  if (agent.updateRequestedAt) parts.push(`Demandee: ${formatDateTime(agent.updateRequestedAt)}`);
  if (agent.updateStartedAt) parts.push(`Demarree: ${formatDateTime(agent.updateStartedAt)}`);
  if (agent.updateCompletedAt) parts.push(`Terminee: ${formatDateTime(agent.updateCompletedAt)}`);
  if (agent.updateTargetVersion) parts.push(`Cible: ${agent.updateTargetVersion}`);
  if (agent.updateMessage) parts.push(agent.updateMessage);
  if (agent.updateError) parts.push(`Erreur: ${agent.updateError}`);
  return parts.join(' | ') || 'Aucune mise a jour demandee';
}

export default function AgentsPage() {
  const [agents, setAgents] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [deploymentDialogOpen, setDeploymentDialogOpen] = useState(false);
  const [tokenDraft, setTokenDraft] = useState({ name: '', organizationId: '', site: '', tags: '' });
  const [deployment, setDeployment] = useState({
    organizationId: '',
    tokenId: '',
    platform: 'windows',
    serverUrl: process.env.REACT_APP_API_URL || 'https://api.omnibox.re',
    site: '',
    tags: '',
  });
  const [newToken, setNewToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [updateDetailsAgent, setUpdateDetailsAgent] = useState(null);
  const [rebootAgent, setRebootAgent] = useState(null);
  const [rebootConfirmText, setRebootConfirmText] = useState('');
  const [windowsUpdateAgent, setWindowsUpdateAgent] = useState(null);
  const [windowsUpdateAllowReboot, setWindowsUpdateAllowReboot] = useState(false);
  const [siteDialogAgent, setSiteDialogAgent] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteValue, setSiteValue] = useState(null);
  const [siteInputValue, setSiteInputValue] = useState('');
  const [deploymentSiteOptions, setDeploymentSiteOptions] = useState([]);
  const [deploymentSiteLoading, setDeploymentSiteLoading] = useState(false);
  const [siteDialogAgents, setSiteDialogAgents] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [filters, setFilters] = useState({ organizationId: '', site: '', os: '', updates: '', activation: '' });
  const [deleteAgent, setDeleteAgent] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [serviceActionPending, setServiceActionPending] = useState('');

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError('');
    try {
      const [agentsRes, tokensRes, orgsRes] = await Promise.all([
        axios.get('/api/agents'),
        axios.get('/api/agents/enrollment-tokens'),
        axios.get('/api/agents/organizations'),
      ]);
      setAgents(Array.isArray(agentsRes.data?.data) ? agentsRes.data.data : []);
      setTokens(Array.isArray(tokensRes.data?.data) ? tokensRes.data.data : []);
      setOrganizations(Array.isArray(orgsRes.data?.data) ? orgsRes.data.data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de charger les agents.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = window.setInterval(() => {
      loadData(true);
    }, 30000);
    return () => window.clearInterval(interval);
  }, [loadData]);

  useEffect(() => {
    let cancelled = false;
    const loadDeploymentSites = async () => {
      if (!deployment.organizationId) {
        setDeploymentSiteOptions([]);
        setDeploymentSiteLoading(false);
        return;
      }
      setDeploymentSiteLoading(true);
      try {
        const { data } = await axios.get(`/api/staff-portfolio/organizations/${deployment.organizationId}/sites`);
        if (!cancelled) setDeploymentSiteOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setDeploymentSiteOptions([]);
      } finally {
        if (!cancelled) setDeploymentSiteLoading(false);
      }
    };
    loadDeploymentSites();
    return () => {
      cancelled = true;
    };
  }, [deployment.organizationId]);

  const createToken = async () => {
    setError('');
    setMessage('');
    try {
      const payload = {
        name: tokenDraft.name,
        organizationId: tokenDraft.organizationId || null,
        site: tokenDraft.site,
        tags: tokenDraft.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      };
      const { data } = await axios.post('/api/agents/enrollment-tokens', payload);
      setNewToken(data.token || '');
      setMessage(data.message || 'Token créé.');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de créer le token.');
    }
  };

  const revokeToken = useCallback(async (id) => {
    setError('');
    try {
      await axios.delete(`/api/agents/enrollment-tokens/${id}`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de révoquer le token.');
    }
  }, [loadData]);

  const copyToken = async () => {
    if (!newToken) return;
    await navigator.clipboard?.writeText(newToken);
    setMessage('Token copié dans le presse-papiers.');
  };


  const activeDeploymentTokens = useMemo(() => tokens.filter((token) => (
    token.enabled
    && !token.revokedAt
    && token.tokenValue
    && (!deployment.organizationId || token.Organization?._id === deployment.organizationId)
  )), [deployment.organizationId, tokens]);

  const selectedDeploymentToken = useMemo(() => {
    if (deployment.tokenId) {
      return activeDeploymentTokens.find((token) => token._id === deployment.tokenId) || null;
    }
    return activeDeploymentTokens[0] || null;
  }, [activeDeploymentTokens, deployment.tokenId]);

  const selectedDeploymentOrg = useMemo(
    () => organizations.find((org) => org._id === deployment.organizationId) || null,
    [deployment.organizationId, organizations]
  );

  const deploymentCommand = useMemo(() => {
    const enrollmentToken = selectedDeploymentToken?.tokenValue || '';
    const serverUrl = (deployment.serverUrl || 'https://api.omnibox.re').replace(/\/$/, '');
    const site = deployment.site || selectedDeploymentToken?.site || selectedDeploymentOrg?.name || '';
    const tags = deployment.tags || 'production';

    if (!enrollmentToken) return '';

    if (deployment.platform === 'linux') {
      const downloadUrl = `${serverUrl}/api/agents/download/linux`;
      return `curl -fsSL -H "x-enrollment-token: ${enrollmentToken}" "${downloadUrl}" -o /tmp/sagent-linux-amd64 && chmod +x /tmp/sagent-linux-amd64 && sudo /tmp/sagent-linux-amd64 install --server-url "${serverUrl}" --enrollment-token "${enrollmentToken}" --site "${site}" --tags "${tags}"`;
    }

    const downloadUrl = `${serverUrl}/api/agents/download/windows`;
    return String.raw`[Net.ServicePointManager]::SecurityProtocol = [Net.SecurityProtocolType]::Tls12; New-Item -ItemType Directory -Force -Path C:\ProgramData\SAgent | Out-Null; $wc = New-Object System.Net.WebClient; $wc.Headers.Add("x-enrollment-token", "${enrollmentToken}"); $wc.DownloadFile("${downloadUrl}", "C:\ProgramData\SAgent\sagent.exe"); & "C:\ProgramData\SAgent\sagent.exe" install --server-url "${serverUrl}" --enrollment-token "${enrollmentToken}" --site "${site}" --tags "${tags}"`;
  }, [deployment.platform, deployment.serverUrl, deployment.site, deployment.tags, selectedDeploymentOrg, selectedDeploymentToken]);

  const copyDeploymentCommand = async () => {
    if (!deploymentCommand) return;
    await navigator.clipboard?.writeText(deploymentCommand);
    setMessage('Commande de déploiement copiée.');
  };

  const downloadAgent = async () => {
    const platform = deployment.platform === 'linux' ? 'linux' : 'windows';
    const fileName = platform === 'linux' ? 'sagent-linux-amd64' : 'sagent.exe';
    const enrollmentToken = selectedDeploymentToken?.tokenValue || '';
    if (!enrollmentToken) {
      setError('Aucun token d enrôlement actif pour télécharger l agent.');
      return;
    }
    setError('');
    try {
      const response = await axios.get(`/api/agents/download/${platform}`, {
        responseType: 'blob',
        headers: { 'x-enrollment-token': enrollmentToken },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de télécharger l agent.');
    }
  };


  const requestAgentUpdate = useCallback(async (agentId) => {
    if (!agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`/api/agents/${agentId}/request-update`);
      setMessage(data.message || 'Mise à jour demandée.');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander la mise à jour.');
    }
  }, [loadData]);

  const requestAgentReboot = useCallback(async () => {
    if (!rebootAgent?.agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`/api/agents/${rebootAgent.agentId}/request-reboot`);
      setMessage(data.message || 'Redémarrage demandé.');
      setRebootAgent(null);
      setRebootConfirmText('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander le redémarrage.');
    }
  }, [loadData, rebootAgent]);

  const requestWindowsUpdate = useCallback(async () => {
    if (!windowsUpdateAgent?.agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`/api/agents/${windowsUpdateAgent.agentId}/request-windows-update`, {
        allowReboot: windowsUpdateAllowReboot,
      });
      setMessage(data.message || 'Windows Update demandé.');
      setWindowsUpdateAgent(null);
      setWindowsUpdateAllowReboot(false);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander Windows Update.');
    }
  }, [loadData, windowsUpdateAgent, windowsUpdateAllowReboot]);

  const requestOSUpdate = useCallback(async (agent, item) => {
    if (!agent?.agentId || !item) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(`/api/agents/${agent.agentId}/request-os-update`, {
        source: agent.lastInventory?.updates?.source || '',
        name: item.name || '',
        kb: item.kb || '',
        version: item.version || '',
        item,
        allowReboot: false,
      });
      setMessage(data.message || 'Installation demandée.');
      await loadData(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander l installation.');
    }
  }, [loadData]);


  const requestServiceAction = useCallback(async (agent, service, action) => {
    if (!agent?.agentId || !service?.name || !['start', 'stop'].includes(action)) return;
    const serviceLabel = service.displayName || service.name;
    const actionLabel = action === 'start' ? 'démarrer' : 'arrêter';
    if (!window.confirm(`Confirmer : ${actionLabel} le service ${serviceLabel} sur ${agent.hostname || agent.agentId} ?`)) return;
    const pendingKey = `${agent.agentId}:${service.name || service.displayName}`;
    setError('');
    setMessage('');
    setServiceActionPending(pendingKey);
    try {
      const { data } = await axios.post(`/api/agents/${agent.agentId}/services/action`, {
        serviceName: service.name,
        action,
      });
      setMessage(data.message || 'Action service demandée.');
      await loadData(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander l action service.');
    } finally {
      setServiceActionPending('');
    }
  }, [loadData]);

  const openSiteDialog = useCallback(async (targetAgents) => {
    const selectedAgents = Array.isArray(targetAgents) ? targetAgents.filter(Boolean) : [targetAgents].filter(Boolean);
    const agent = selectedAgents[0];
    if (!agent) return;
    const orgIds = new Set(selectedAgents.map(getAgentOrgId).filter(Boolean));
    const canLoadHuduSites = orgIds.size <= 1;
    setSiteDialogAgent(agent);
    setSiteDialogAgents(selectedAgents);
    setSiteValue(selectedAgents.length === 1 ? (agent.siteRef || null) : null);
    setSiteInputValue(selectedAgents.length === 1 ? (agent.site || '') : '');
    setSiteOptions([]);
    setSiteLoading(canLoadHuduSites);
    setError('');
    if (!canLoadHuduSites) return;
    try {
      const { data } = await axios.get(`/api/agents/${agent.agentId}/sites`);
      const options = Array.isArray(data?.data) ? data.data : [];
      setSiteOptions(options);
      const current = options.find((site) => site._id === agent.siteRef?._id || site._id === agent.siteRef);
      if (current) {
        setSiteValue(current);
        setSiteInputValue(current.name || '');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de charger les sites.');
    } finally {
      setSiteLoading(false);
    }
  }, []);

  const saveAgentSite = useCallback(async () => {
    const targetAgents = siteDialogAgents.length ? siteDialogAgents : [siteDialogAgent].filter(Boolean);
    if (targetAgents.length === 0) return;
    setError('');
    setMessage('');
    try {
      const selectedSite = siteValue && typeof siteValue === 'object' ? siteValue : null;
      const payload = selectedSite?._id
        ? { siteId: selectedSite._id }
        : { siteName: siteInputValue.trim() };
      await Promise.all(targetAgents.map((agent) => axios.put(`/api/agents/${agent.agentId}/site`, payload)));
      setMessage(targetAgents.length > 1 ? `${targetAgents.length} device(s) mis à jour.` : 'Site mis à jour.');
      setSiteDialogAgent(null);
      setSiteDialogAgents([]);
      setSiteValue(null);
      setSiteInputValue('');
      setRowSelection({});
      await loadData(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de modifier le site.');
    }
  }, [loadData, siteDialogAgent, siteDialogAgents, siteInputValue, siteValue]);

  const requestAgentDelete = useCallback(async () => {
    if (!deleteAgent?.agentId) return;
    setError('');
    setMessage('');
    try {
      const { data } = await axios.delete(`/api/agents/${deleteAgent.agentId}`);
      setMessage(data.message || 'Agent supprimé.');
      setDeleteAgent(null);
      setDeleteConfirmText('');
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de supprimer l agent.');
    }
  }, [deleteAgent, loadData]);

  const filterOrganizationOptions = useMemo(() => {
    const map = new Map();
    agents.forEach((agent) => {
      const id = getAgentOrgId(agent);
      const name = getAgentOrgName(agent);
      if (id) map.set(id, name);
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [agents]);

  const filterOSOptions = useMemo(() => {
    const values = new Set();
    agents.forEach((agent) => values.add(getAgentOsLabel(agent)));
    return [...values].filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [agents]);

  const filterSiteOptions = useMemo(() => {
    const values = new Set();
    agents.forEach((agent) => values.add(getAgentSiteLabel(agent)));
    return [...values].filter(Boolean).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [agents]);

  const filteredAgents = useMemo(() => agents.filter((agent) => {
    if (filters.organizationId && getAgentOrgId(agent) !== filters.organizationId) return false;
    if (filters.site && getAgentSiteLabel(agent) !== filters.site) return false;
    if (filters.os && getAgentOsLabel(agent) !== filters.os) return false;
    if (filters.updates === 'pending' && getPendingUpdateCount(agent) <= 0) return false;
    if (filters.updates === 'none' && getPendingUpdateCount(agent) > 0) return false;
    if (filters.activation && getActivationFilterValue(agent) !== filters.activation) return false;
    return true;
  }), [agents, filters.organizationId, filters.site, filters.os, filters.updates, filters.activation]);

  const selectedAgents = useMemo(() => {
    const selectedIds = new Set(Object.keys(rowSelection).filter((key) => rowSelection[key]));
    if (selectedIds.size === 0) return [];
    return agents.filter((agent) => selectedIds.has(agent.agentId));
  }, [agents, rowSelection]);

  const selectedUpdateableAgents = useMemo(() => (
    selectedAgents.filter((agent) => Boolean(agent.agentUpdateAvailable))
  ), [selectedAgents]);

  const selectedAlreadyUpdatedCount = selectedAgents.length - selectedUpdateableAgents.length;

  const requestSelectedAgentUpdates = useCallback(async () => {
    if (selectedUpdateableAgents.length === 0) {
      setMessage(selectedAgents.length > 0 ? 'Aucun agent sélectionné à mettre à jour.' : '');
      return;
    }
    setBulkUpdating(true);
    setError('');
    setMessage('');
    try {
      const results = await Promise.allSettled(selectedUpdateableAgents.map((agent) => (
        axios.post(`/api/agents/${agent.agentId}/request-update`)
      )));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failed = results
        .map((result, index) => ({ result, agent: selectedUpdateableAgents[index] }))
        .filter((item) => item.result.status === 'rejected');
      if (failed.length) {
        setError(`${failed.length} mise(s) à jour non demandée(s) : ${failed.map((item) => item.agent.hostname).join(', ')}`);
      }
      const ignored = selectedAlreadyUpdatedCount > 0 ? `, ${selectedAlreadyUpdatedCount} déjà à jour ignoré(s)` : '';
      setMessage(`${successCount} mise(s) à jour demandée(s)${ignored}.`);
      setRowSelection({});
      await loadData(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Impossible de demander les mises à jour sélectionnées.');
    } finally {
      setBulkUpdating(false);
    }
  }, [loadData, selectedAgents.length, selectedAlreadyUpdatedCount, selectedUpdateableAgents]);

  const agentColumns = useMemo(() => [
    {
      accessorKey: 'computedStatus',
      header: 'Statut',
      size: 110,
      Cell: ({ row }) => <Chip size="small" color={getStatusColor(row.original.computedStatus)} label={row.original.computedStatus || 'unknown'} />,
    },
    { accessorKey: 'hostname', header: 'Hostname', size: 190 },
    {
      id: 'client',
      accessorFn: (row) => getAgentOrgName(row),
      header: 'Client',
      size: 220,
      Cell: ({ row }) => row.original.Organization?.name || '-',
    },
    {
      id: 'site',
      accessorFn: (row) => getAgentSiteLabel(row),
      header: 'Site',
      size: 190,
      Cell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
              {row.original.site || '-'}
            </Typography>
            {row.original.siteCustom ? <Chip size="small" variant="outlined" label="Custom" sx={{ height: 18, fontSize: 10 }} /> : row.original.siteRef ? <Chip size="small" color="success" variant="outlined" label="Hudu" sx={{ height: 18, fontSize: 10 }} /> : null}
          </Box>
          <Tooltip title="Modifier le site">
            <IconButton size="small" onClick={() => openSiteDialog(row.original)}>
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      accessorKey: 'os.name',
      header: 'OS',
      size: 250,
      Cell: ({ row }) => [row.original.os?.name, row.original.os?.version, row.original.os?.arch].filter(Boolean).join(' ') || '-',
    },
    {
      accessorKey: 'lastInventory.hardware.vendor',
      header: 'Vendor',
      size: 150,
      Cell: ({ row }) => row.original.lastInventory?.hardware?.vendor || row.original.lastInventory?.hardware?.manufacturer || '-',
    },
    {
      accessorKey: 'lastInventory.hardware.model',
      header: 'Modèle',
      size: 170,
      Cell: ({ row }) => row.original.lastInventory?.hardware?.model || row.original.lastInventory?.hardware?.productName || '-',
    },
    {
      accessorKey: 'lastInventory.hardware.serialNumber',
      header: 'Serial',
      size: 150,
      Cell: ({ cell }) => cell.getValue() || '-',
    },
    {
      id: 'localIps',
      header: 'IP locales',
      size: 240,
      accessorFn: (row) => getAgentLocalIps(row).join(' '),
      Cell: ({ row }) => {
        const ips = getAgentLocalIps(row.original);
        return ips.length ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {ips.slice(0, 5).map((ip) => <Chip key={ip} size="small" variant="outlined" label={ip} />)}
            {ips.length > 5 ? <Chip size="small" variant="outlined" label={`+${ips.length - 5}`} /> : null}
          </Stack>
        ) : '-';
      },
    },
    { accessorKey: 'lastRemoteAddress', header: 'IP publique', size: 140 },
    {
      accessorKey: 'lastUptimeSeconds',
      header: 'Uptime',
      size: 120,
      Cell: ({ cell }) => formatUptime(cell.getValue()),
    },
    {
      id: 'loggedUsers',
      header: 'User loggé',
      size: 180,
      accessorFn: (row) => getLoggedUsers(row).map(formatLoggedUser).join(' '),
      Cell: ({ row }) => {
        const users = getLoggedUsers(row.original);
        return users.length > 0 ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {users.slice(0, 2).map((user, index) => <Chip key={`${formatLoggedUser(user)}-${index}`} size="small" variant="outlined" label={formatLoggedUser(user)} />)}
            {users.length > 2 ? <Chip size="small" variant="outlined" label={`+${users.length - 2}`} /> : null}
          </Stack>
        ) : '-';
      },
    },
    {
      id: 'osAge',
      header: 'Âge OS',
      size: 120,
      accessorFn: (row) => row.lastInventory?.os?.installAgeDays || row.os?.installAgeDays || 0,
      Cell: ({ row }) => {
        const osInfo = row.original.lastInventory?.os || row.original.os || {};
        return osInfo.installDate ? (
          <Tooltip title={`Installé le ${formatDateTime(osInfo.installDate)}`}>
            <span>{formatOsAge(osInfo.installAgeDays)}</span>
          </Tooltip>
        ) : '-';
      },
    },
    {
      id: 'windowsActivation',
      header: 'Activation',
      size: 125,
      accessorFn: (row) => getActivationMeta(row).label,
      Cell: ({ row }) => {
        const meta = getActivationMeta(row.original);
        const activation = getWindowsActivation(row.original);
        return (
          <Tooltip title={[meta.title, activation?.productName || '', activation?.partialProductKey ? `Key ${activation.partialProductKey}` : ''].filter(Boolean).join(' · ')}>
            <Chip size="small" color={meta.color} variant={meta.variant} label={meta.label} />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'lastInventory.updates.pendingCount',
      header: 'MAJ',
      size: 110,
      Cell: ({ row }) => {
        const updates = row.original.lastInventory?.updates || {};
        const count = Number(updates.pendingCount ?? 0);
        const hasWarnings = Array.isArray(updates.warnings) && updates.warnings.length > 0;
        return (
          <Tooltip title={hasWarnings ? updates.warnings.join(' | ') : updates.source || ''}>
            <Chip
              size="small"
              color={count > 0 ? 'warning' : hasWarnings ? 'default' : 'success'}
              variant={count > 0 ? 'filled' : 'outlined'}
              label={hasWarnings && !count ? 'N/A' : `${count} MAJ`}
            />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'lastMetrics.cpu.usagePercent',
      header: 'CPU',
      size: 120,
      Cell: ({ row }) => row.original.lastMetrics?.cpu?.usagePercent != null ? `${Number(row.original.lastMetrics.cpu.usagePercent).toFixed(1)} %` : '-',
    },
    {
      accessorKey: 'lastMetrics.memory.usedBytes',
      header: 'RAM',
      size: 150,
      Cell: ({ row }) => {
        const memory = row.original.lastMetrics?.memory || row.original.lastInventory?.memory || {};
        return memory.usedBytes ? `${formatBytes(memory.usedBytes)} / ${formatBytes(memory.totalBytes)}` : formatBytes(memory.totalBytes);
      },
    },
    {
      accessorKey: 'lastSeenAt',
      header: 'Dernière remontée',
      size: 190,
      Cell: ({ cell }) => formatDateTime(cell.getValue()),
    },
    {
      accessorKey: 'agentVersion',
      header: 'Version',
      size: 100,
      Cell: ({ cell }) => <Typography variant="body2" sx={{ fontWeight: 700 }}>{cell.getValue() || '-'}</Typography>,
    },
    {
      accessorKey: 'updateStatus',
      header: 'Update',
      size: 170,
      Cell: ({ row }) => {
        const status = row.original.updateStatus || 'idle';
        const hasProcess = ['pending', 'acknowledged', 'checking', 'restarting', 'success', 'failed', 'up_to_date'].includes(status);
        const hasUpdate = Boolean(row.original.agentUpdateAvailable);
        const latest = row.original.latestAgentVersion || '-';
        const meta = hasProcess ? getUpdateStatusMeta(status) : hasUpdate ? { label: `MAJ ${latest}`, color: 'warning' } : { label: 'A jour', color: 'success' };
        const tooltip = hasProcess ? getUpdateTooltip(row.original) : hasUpdate ? `Dernière version disponible: ${latest}` : `Dernière version: ${latest}`;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={tooltip}>
              <Chip size="small" color={meta.color} variant={hasUpdate || hasProcess ? 'filled' : 'outlined'} label={meta.label} />
            </Tooltip>
            <Tooltip title="Voir les étapes">
              <span>
                <IconButton size="small" onClick={() => setUpdateDetailsAgent(row.original)}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
    {
      accessorKey: 'windowsUpdateStatus',
      header: 'Windows Update',
      size: 165,
      Cell: ({ row }) => {
        const meta = getWindowsUpdateStatusMeta(row.original.windowsUpdateStatus);
        const tooltip = [
          row.original.windowsUpdateMessage || '',
          row.original.windowsUpdateError ? `Erreur: ${row.original.windowsUpdateError}` : '',
          row.original.windowsUpdateInstalledCount ? `${row.original.windowsUpdateInstalledCount} MAJ installée(s)` : '',
          row.original.windowsUpdateRebootRequired ? 'Redémarrage requis' : '',
          row.original.windowsUpdateCompletedAt ? `Terminé: ${formatDateTime(row.original.windowsUpdateCompletedAt)}` : '',
        ].filter(Boolean).join(' | ') || 'Lancer Windows Update';
        return (
          <Tooltip title={tooltip}>
            <Chip size="small" color={meta.color} variant={row.original.windowsUpdateStatus && row.original.windowsUpdateStatus !== 'idle' ? 'filled' : 'outlined'} label={meta.label} />
          </Tooltip>
        );
      },
    },
    {
      id: 'agentActions',
      header: 'Actions',
      size: 190,
      minSize: 180,
      maxSize: 220,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const canUpdate = Boolean(row.original.agentUpdateAvailable);
        const title = canUpdate
          ? row.original.updateRequestedAt
            ? `Demandée le ${formatDateTime(row.original.updateRequestedAt)}`
            : `Mettre à jour vers ${row.original.latestAgentVersion || 'la dernière version'}`
          : 'Agent déjà à jour';
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 176, whiteSpace: 'nowrap' }}>
            <Tooltip title={title}>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!canUpdate}
                  onClick={() => requestAgentUpdate(row.original.agentId)}
                >
                  <SystemUpdateAltRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Lancer Windows Update">
              <span>
                <IconButton
                  size="small"
                  color="secondary"
                  disabled={row.original.computedStatus !== 'online'}
                  onClick={() => { setWindowsUpdateAgent(row.original); setWindowsUpdateAllowReboot(false); }}
                >
                  <UpdateRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redémarrer le device">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={row.original.computedStatus !== 'online'}
                  onClick={() => { setRebootAgent(row.original); setRebootConfirmText(''); }}
                >
                  <RestartAltRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Supprimer de la liste">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => { setDeleteAgent(row.original); setDeleteConfirmText(''); }}
                >
                  <DeleteRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ], [openSiteDialog, requestAgentUpdate]);

  const tokenColumns = useMemo(() => [
    { accessorKey: 'name', header: 'Nom', size: 180 },
    {
      accessorKey: 'Organization.name',
      header: 'Client',
      size: 220,
      Cell: ({ row }) => row.original.Organization?.name || '-',
    },
    { accessorKey: 'site', header: 'Site', size: 120 },
    {
      accessorKey: 'enabled',
      header: 'Actif',
      size: 100,
      Cell: ({ row }) => <Chip size="small" color={row.original.enabled && !row.original.revokedAt ? 'success' : 'default'} label={row.original.enabled && !row.original.revokedAt ? 'Oui' : 'Non'} />,
    },
    { accessorKey: 'tokenPrefix', header: 'Préfixe', size: 120 },
    {
      accessorKey: 'tokenValue',
      header: 'Token',
      size: 320,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const token = row.original.tokenValue || '';
        if (!token) return <Typography variant="caption" color="text.secondary">Non disponible</Typography>;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={token}>
              <Box component="code" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', fontSize: 12 }}>
                {token}
              </Box>
            </Tooltip>
            <Tooltip title="Copier">
              <IconButton size="small" onClick={() => navigator.clipboard?.writeText(token)}>
                <ContentCopyRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
    { accessorKey: 'useCount', header: 'Usages', size: 90 },
    {
      accessorKey: 'lastUsedAt',
      header: 'Dernier usage',
      size: 180,
      Cell: ({ cell }) => formatDateTime(cell.getValue()),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 90,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Tooltip title="Révoquer">
          <span>
            <IconButton size="small" color="error" disabled={Boolean(row.original.revokedAt)} onClick={() => revokeToken(row.original._id)}>
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ], [revokeToken]);

  const agentTable = useMaterialReactTable({
    columns: agentColumns,
    data: filteredAgents,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    enableGrouping: true,
    enableRowSelection: true,
    groupedColumnMode: 'remove',
    onRowSelectionChange: setRowSelection,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: 'compact',
      sorting: [{ id: 'hostname', desc: false }],
      pagination: { pageIndex: 0, pageSize: 25 },
      columnVisibility: {
        'lastInventory.hardware.vendor': false,
        'lastInventory.hardware.model': false,
        'lastInventory.hardware.serialNumber': false,
        lastRemoteAddress: false,
        'lastMetrics.cpu.usagePercent': false,
        'lastMetrics.memory.usedBytes': false,
        lastSeenAt: false,
      },
    },
    state: { isLoading: loading, rowSelection },
    getRowId: (row) => row.agentId,
    enableExpanding: true,
    positionExpandColumn: 'first',
    renderDetailPanel: ({ row }) => <AgentInventoryPanel agent={row.original} onRequestOSUpdate={requestOSUpdate} onRequestServiceAction={requestServiceAction} serviceActionPending={serviceActionPending} />,
    renderTopToolbarCustomActions: () => (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          disabled={selectedAgents.length === 0}
          startIcon={<EditRoundedIcon />}
          onClick={() => openSiteDialog(selectedAgents)}
        >
          Affecter un site
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={selectedUpdateableAgents.length === 0 || bulkUpdating}
          startIcon={<SystemUpdateAltRoundedIcon />}
          onClick={requestSelectedAgentUpdates}
        >
          {bulkUpdating ? 'Demande...' : 'Mettre à jour sélection'}
        </Button>
        {selectedAgents.length > 0 ? <Chip size="small" label={`${selectedAgents.length} sélectionné(s)`} /> : null}
        {selectedUpdateableAgents.length > 0 ? <Chip size="small" color="warning" label={`${selectedUpdateableAgents.length} à mettre à jour`} /> : null}
        {selectedAlreadyUpdatedCount > 0 ? <Chip size="small" variant="outlined" color="success" label={`${selectedAlreadyUpdatedCount} déjà à jour`} /> : null}
      </Stack>
    ),
  });

  const tokenTable = useMaterialReactTable({
    columns: tokenColumns,
    data: tokens,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableColumnResizing: true,
    initialState: { showColumnFilters: true, showGlobalFilter: true, density: 'compact', pagination: { pageIndex: 0, pageSize: 10 } },
    state: { isLoading: loading },
    getRowId: (row) => row._id,
  });

  const siteDialogOrgCount = new Set(siteDialogAgents.map(getAgentOrgId).filter(Boolean)).size;
  const siteDialogDeviceCount = siteDialogAgents.length || (siteDialogAgent ? 1 : 0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <PageHeader section="Infrastructure" title="Agents" sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={loadData} disabled={loading}>Actualiser</Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setNewToken(''); setTokenDialogOpen(true); }}>Créer un token</Button>
          </Stack>
        </Stack>

        {message ? <Alert severity="success">{message}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Alert
          severity="success"
          variant="outlined"
          sx={{
            display: 'none',
            borderRadius: 2,
            alignItems: 'flex-start',
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                Release notes AgentBOX
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dernières nouveautés disponibles dans les agents Windows et Linux.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
              {AGENT_RELEASE_NOTES.map((release) => (
                <Paper
                  key={release.version}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    flex: 1,
                    bgcolor: 'rgba(46, 125, 50, 0.04)',
                    borderColor: 'success.light',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip size="small" color="success" label={`v${release.version}`} sx={{ fontWeight: 800 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                        {release.title}
                      </Typography>
                    </Stack>
                    <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                      {release.items.map((item) => (
                        <Typography key={item} component="li" variant="body2" sx={{ mb: 0.5 }}>
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </Alert>

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Client"
              value={filters.organizationId}
              onChange={(event) => {
                setFilters((current) => ({ ...current, organizationId: event.target.value }));
                setRowSelection({});
              }}
              sx={{ minWidth: 280 }}
            >
              <MenuItem value="">Tous les clients</MenuItem>
              {filterOrganizationOptions.map((org) => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="Site"
              value={filters.site}
              onChange={(event) => {
                setFilters((current) => ({ ...current, site: event.target.value }));
                setRowSelection({});
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Tous les sites</MenuItem>
              {filterSiteOptions.map((site) => <MenuItem key={site} value={site}>{site}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="OS"
              value={filters.os}
              onChange={(event) => {
                setFilters((current) => ({ ...current, os: event.target.value }));
                setRowSelection({});
              }}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="">Tous les OS</MenuItem>
              {filterOSOptions.map((os) => <MenuItem key={os} value={os}>{os}</MenuItem>)}
            </TextField>
            <TextField
              select
              size="small"
              label="MAJ"
              value={filters.updates}
              onChange={(event) => {
                setFilters((current) => ({ ...current, updates: event.target.value }));
                setRowSelection({});
              }}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value="">Toutes les MAJ</MenuItem>
              <MenuItem value="pending">MAJ à faire</MenuItem>
              <MenuItem value="none">Aucune MAJ</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Activation Windows"
              value={filters.activation}
              onChange={(event) => {
                setFilters((current) => ({ ...current, activation: event.target.value }));
                setRowSelection({});
              }}
              sx={{ minWidth: 210 }}
            >
              <MenuItem value="">Toutes activations</MenuItem>
              <MenuItem value="active">Activé</MenuItem>
              <MenuItem value="inactive">Pas activé</MenuItem>
              <MenuItem value="unknown">Inconnu</MenuItem>
              <MenuItem value="not_applicable">Non applicable</MenuItem>
            </TextField>
            <Chip variant="outlined" label={`${filteredAgents.length} ligne(s)`} />
            {(filters.organizationId || filters.site || filters.os || filters.updates || filters.activation) ? (
              <Button size="small" onClick={() => { setFilters({ organizationId: '', site: '', os: '', updates: '', activation: '' }); setRowSelection({}); }}>
                Réinitialiser
              </Button>
            ) : null}
          </Stack>
        </Paper>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${filteredAgents.length} agent(s)`} />
          <Chip color="success" variant="outlined" label={`${filteredAgents.filter((agent) => agent.computedStatus === 'online').length} online`} />
          <Chip color="error" variant="outlined" label={`${filteredAgents.filter((agent) => agent.computedStatus === 'offline').length} offline`} />
          <Chip variant="outlined" label={`${tokens.filter((token) => token.enabled && !token.revokedAt).length} token(s) actif(s)`} />
        </Stack>


        <Dialog open={deploymentDialogOpen} onClose={() => setDeploymentDialogOpen(false)} fullWidth maxWidth="lg">
          <DialogTitle>Déployer un agent</DialogTitle>
          <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>Déployer un agent</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sélectionne le client puis copie la commande sur la machine. Elle télécharge l agent avec le token d enrôlement puis l installe automatiquement.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={downloadAgent}>Télécharger</Button>
                <Button variant="contained" startIcon={<ContentCopyRoundedIcon />} disabled={!deploymentCommand} onClick={copyDeploymentCommand}>Copier la commande</Button>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.25}
              flexWrap="wrap"
              useFlexGap
              alignItems={{ lg: 'flex-start' }}
            >
              <TextField
                select
                size="small"
                label="Client"
                value={deployment.organizationId}
                onChange={(e) => setDeployment((draft) => ({ ...draft, organizationId: e.target.value, tokenId: '', site: '' }))}
                sx={{ minWidth: 220, flex: '1 1 220px' }}
              >
                <MenuItem value="">Tous les clients</MenuItem>
                {organizations.map((org) => <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="Token"
                value={selectedDeploymentToken?._id || ''}
                onChange={(e) => setDeployment((draft) => ({ ...draft, tokenId: e.target.value }))}
                sx={{ minWidth: 240, flex: '1 1 240px' }}
              >
                {activeDeploymentTokens.length === 0 ? <MenuItem value="">Aucun token actif disponible</MenuItem> : null}
                {activeDeploymentTokens.map((token) => (
                  <MenuItem key={token._id} value={token._id}>{token.name || token.tokenPrefix} {token.site ? `- ${token.site}` : ''}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="OS"
                value={deployment.platform}
                onChange={(e) => setDeployment((draft) => ({ ...draft, platform: e.target.value }))}
                sx={{ minWidth: 130, flex: '0 1 130px' }}
              >
                <MenuItem value="windows">Windows</MenuItem>
                <MenuItem value="linux">Linux</MenuItem>
              </TextField>
              <TextField
                size="small"
                label="URL serveur"
                value={deployment.serverUrl}
                onChange={(e) => setDeployment((draft) => ({ ...draft, serverUrl: e.target.value }))}
                sx={{ minWidth: 240, flex: '1 1 240px' }}
              />
              <Autocomplete
                freeSolo
                size="small"
                loading={deploymentSiteLoading}
                options={deploymentSiteOptions}
                value={deploymentSiteOptions.find((site) => site.name === deployment.site) || null}
                inputValue={deployment.site}
                onChange={(event, value) => {
                  if (typeof value === 'string') setDeployment((draft) => ({ ...draft, site: value }));
                  else setDeployment((draft) => ({ ...draft, site: value?.name || '' }));
                }}
                onInputChange={(event, value) => setDeployment((draft) => ({ ...draft, site: value }))}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.name || '';
                }}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.name || '-'}</Typography>
                      {option.address ? <Typography variant="caption" color="text.secondary">{option.address}</Typography> : null}
                    </Stack>
                  </Box>
                )}
                renderInput={(params) => <TextField {...params} label="Site" placeholder="Hudu ou custom" />}
                sx={{ minWidth: 220, flex: '1 1 220px' }}
              />
              <TextField
                size="small"
                label="Tags"
                placeholder="production,serveur"
                value={deployment.tags}
                onChange={(e) => setDeployment((draft) => ({ ...draft, tags: e.target.value }))}
                sx={{ minWidth: 200, flex: '1 1 200px' }}
              />
            </Stack>

            {!deploymentCommand ? (
              <Alert severity="info">Aucun token actif et lisible pour ce client. Crée un nouveau token si besoin.</Alert>
            ) : (
              <Box component="code" sx={{ display: 'block', p: 1.25, bgcolor: 'grey.100', borderRadius: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, maxHeight: 86, overflow: 'auto' }}>
                {deploymentCommand}
              </Box>
            )}
          </Stack>
          </DialogContent>
          <DialogActions><Button onClick={() => setDeploymentDialogOpen(false)}>Fermer</Button></DialogActions>
        </Dialog>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Machines supervisées</Typography>
            <MaterialReactTable table={agentTable} />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Tokens d'enrôlement</Typography>
            <MaterialReactTable table={tokenTable} />
          </Stack>
        </Paper>
      </Stack>

        <Dialog open={Boolean(updateDetailsAgent)} onClose={() => setUpdateDetailsAgent(null)} fullWidth maxWidth="sm">
          <DialogTitle>Étapes de mise à jour</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.25}>
              <Typography variant="body2" color="text.secondary">
                {updateDetailsAgent?.hostname || '-'} - {updateDetailsAgent?.agentVersion || '-'} vers {updateDetailsAgent?.updateTargetVersion || updateDetailsAgent?.latestAgentVersion || '-'}
              </Typography>
              {(Array.isArray(updateDetailsAgent?.updateHistory) && updateDetailsAgent.updateHistory.length > 0 ? updateDetailsAgent.updateHistory : [{
                status: updateDetailsAgent?.updateStatus,
                message: updateDetailsAgent?.updateMessage,
                error: updateDetailsAgent?.updateError,
                currentVersion: updateDetailsAgent?.updateCurrentVersion,
                targetVersion: updateDetailsAgent?.updateTargetVersion,
                at: updateDetailsAgent?.lastUpdateCheckAt || updateDetailsAgent?.updateRequestedAt,
              }]).map((step, index) => {
                const meta = getUpdateStatusMeta(step.status);
                return (
                  <Paper key={`${step.status || 'step'}-${index}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" color={meta.color} label={getStepStatusLabel(step.status)} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{step.message || '-'}</Typography>
                        {step.error ? <Typography variant="body2" color="error">{step.error}</Typography> : null}
                        <Typography variant="caption" color="text.secondary">
                          {[formatDateTime(step.at), step.currentVersion ? `Actuelle: ${step.currentVersion}` : '', step.targetVersion ? `Cible: ${step.targetVersion}` : ''].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUpdateDetailsAgent(null)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deleteAgent)} onClose={() => setDeleteAgent(null)} fullWidth maxWidth="xs">
          <DialogTitle>Supprimer l agent</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Alert severity="error">Cette action retire {deleteAgent?.hostname || 'cet agent'} de la liste. Les historiques restent conservés.</Alert>
              <Typography variant="body2" color="text.secondary">Tape DELETE pour confirmer.</Typography>
              <TextField
                autoFocus
                label="Confirmation"
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value)}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteAgent(null)}>Annuler</Button>
            <Button color="error" variant="contained" disabled={deleteConfirmText !== 'DELETE'} onClick={requestAgentDelete}>Supprimer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(rebootAgent)} onClose={() => setRebootAgent(null)} fullWidth maxWidth="xs">
          <DialogTitle>Confirmer le redémarrage</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Alert severity="warning">Cette action va redémarrer le device {rebootAgent?.hostname || ''}.</Alert>
              <Typography variant="body2" color="text.secondary">Tape REBOOT pour confirmer.</Typography>
              <TextField
                autoFocus
                label="Confirmation"
                value={rebootConfirmText}
                onChange={(event) => setRebootConfirmText(event.target.value)}
                fullWidth
              />
              {rebootAgent?.rebootStatus && rebootAgent.rebootStatus !== 'idle' ? (
                <Chip size="small" color={getRebootStatusMeta(rebootAgent.rebootStatus).color} label={getRebootStatusMeta(rebootAgent.rebootStatus).label} sx={{ alignSelf: 'flex-start' }} />
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRebootAgent(null)}>Annuler</Button>
            <Button color="warning" variant="contained" disabled={rebootConfirmText !== 'REBOOT'} onClick={requestAgentReboot}>Redémarrer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(windowsUpdateAgent)} onClose={() => setWindowsUpdateAgent(null)} fullWidth maxWidth="xs">
          <DialogTitle>Lancer Windows Update</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Alert severity="info">
                L agent {windowsUpdateAgent?.hostname || ''} lancera Windows Update au prochain heartbeat. L opération peut prendre plusieurs minutes.
              </Alert>
              <FormControlLabel
                control={(
                  <Switch
                    checked={windowsUpdateAllowReboot}
                    onChange={(event) => setWindowsUpdateAllowReboot(event.target.checked)}
                  />
                )}
                label="Autoriser le redémarrage si Windows le demande"
              />
              {windowsUpdateAgent?.windowsUpdateStatus && windowsUpdateAgent.windowsUpdateStatus !== 'idle' ? (
                <Stack spacing={0.75}>
                  <Chip
                    size="small"
                    color={getWindowsUpdateStatusMeta(windowsUpdateAgent.windowsUpdateStatus).color}
                    label={getWindowsUpdateStatusMeta(windowsUpdateAgent.windowsUpdateStatus).label}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  {windowsUpdateAgent.windowsUpdateMessage ? <Typography variant="body2">{windowsUpdateAgent.windowsUpdateMessage}</Typography> : null}
                  {windowsUpdateAgent.windowsUpdateError ? <Alert severity="error">{windowsUpdateAgent.windowsUpdateError}</Alert> : null}
                </Stack>
              ) : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWindowsUpdateAgent(null)}>Annuler</Button>
            <Button color="secondary" variant="contained" onClick={requestWindowsUpdate}>Lancer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(siteDialogAgent)} onClose={() => { setSiteDialogAgent(null); setSiteDialogAgents([]); }} fullWidth maxWidth="sm">
          <DialogTitle>{siteDialogDeviceCount > 1 ? 'Affecter un site' : 'Modifier le site'}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                {siteDialogDeviceCount > 1
                  ? `${siteDialogDeviceCount} device(s) sélectionné(s)`
                  : `${siteDialogAgent?.hostname || '-'} · ${siteDialogAgent?.Organization?.name || 'Client non rattaché'}`}
              </Typography>
              {siteDialogDeviceCount > 1 && siteDialogOrgCount > 1 ? (
                <Alert severity="warning">Plusieurs clients sont sélectionnés : seuls les sites custom sont recommandés ici.</Alert>
              ) : null}
              <Autocomplete
                freeSolo
                loading={siteLoading}
                options={siteOptions}
                value={siteValue}
                inputValue={siteInputValue}
                onChange={(event, value) => {
                  setSiteValue(typeof value === 'string' ? null : value);
                  if (typeof value === 'string') setSiteInputValue(value);
                  else setSiteInputValue(value?.name || '');
                }}
                onInputChange={(event, value) => {
                  setSiteInputValue(value);
                  if (!value) setSiteValue(null);
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.name || '';
                }}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.name || '-'}</Typography>
                      {option.address ? <Typography variant="caption" color="text.secondary">{option.address}</Typography> : null}
                    </Stack>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Site"
                    helperText="Choisis un site Hudu ou saisis un site custom."
                    fullWidth
                  />
                )}
              />
              {siteValue?.address ? <Alert severity="info">{siteValue.address}</Alert> : null}
              {!siteValue && siteInputValue.trim() ? <Alert severity="warning">Ce site sera enregistré comme valeur custom sur l agent.</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setSiteDialogAgent(null); setSiteDialogAgents([]); }}>Annuler</Button>
            <Button onClick={() => { setSiteValue(null); setSiteInputValue(''); }}>Vider</Button>
            <Button variant="contained" onClick={saveAgentSite}>Enregistrer</Button>
          </DialogActions>
        </Dialog>


      <Drawer anchor="right" open={tokenDialogOpen} onClose={() => setTokenDialogOpen(false)}>
        <DialogTitle>Créer un token d'enrôlement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Nom" value={tokenDraft.name} onChange={(e) => setTokenDraft((draft) => ({ ...draft, name: e.target.value }))} fullWidth />
            <TextField select label="Client" value={tokenDraft.organizationId} onChange={(e) => setTokenDraft((draft) => ({ ...draft, organizationId: e.target.value }))} fullWidth>
              <MenuItem value="">Aucun client</MenuItem>
              {organizations.map((org) => <MenuItem key={org._id} value={org._id}>{org.name}</MenuItem>)}
            </TextField>
            <TextField label="Site" value={tokenDraft.site} onChange={(e) => setTokenDraft((draft) => ({ ...draft, site: e.target.value }))} fullWidth />
            <TextField label="Tags" helperText="Sépare les tags par des virgules." value={tokenDraft.tags} onChange={(e) => setTokenDraft((draft) => ({ ...draft, tags: e.target.value }))} fullWidth />
            {newToken ? (
              <Alert severity="warning">
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>Token créé</Typography>
                  <Box component="code" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1, wordBreak: 'break-all' }}>{newToken}</Box>
                  <Button size="small" startIcon={<ContentCopyRoundedIcon />} onClick={copyToken}>Copier le token</Button>
                </Stack>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenDialogOpen(false)}>Fermer</Button>
          <Button variant="contained" onClick={createToken}>Créer</Button>
        </DialogActions>
      </Drawer>
    </Box>
  );
}
